import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, UserRound, Mail, ShieldCheck, Blocks, Pencil, Save, MonitorSmartphone, Globe } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { USER_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';

const Profile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [username, setUsername] = useState("");
    const [avatar, setAvatar] = useState("");

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => setAlerts(prev => ([...prev, { id: v4(), text }]));

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await fetch(USER_URL + "/users", {
                    method: "GET",
                    credentials: "include",
                    headers: buildHeaders(),
                });
                const data = await res.json();

                if (!res.ok || data.failed) {
                    if (data.signout) navigate("/auth");
                    pushAlert(data.message || "Oops! something went wrong while loading your profile.");
                    return;
                }

                setUser(data.data);
                setUsername(data.data.username || "");
                setAvatar(data.data.avatar || "");
            } catch (err) {
                pushAlert("Oops! something went wrong while reaching the server.");
            } finally {
                setLoading(false);
            }
        }
        fetchUser();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const newUserData = { username: username.trim() };
            if (avatar.trim()) newUserData.avatar = avatar.trim();

            const res = await fetch(USER_URL + "/users", {
                method: "PATCH",
                credentials: "include",
                headers: buildHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ newUserData }),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while saving your profile.");
                return;
            }

            pushAlert("Your data has been updated successfully!");
            setUser(prev => ({ ...prev, username: username.trim(), avatar: avatar.trim() || prev.avatar }));
            setEditing(false);
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setSaving(false);
        }
    }

    const cancelEdit = () => {
        setUsername(user?.username || "");
        setAvatar(user?.avatar || "");
        setEditing(false);
    }

    return (
        <div className="page">
            <div className="page_header">
                <button className="back" onClick={() => navigate("/")}><ChevronLeft /></button>
                <div className="page_title">
                    <h2>Profile</h2>
                    <p>Your account, at a glance.</p>
                </div>
            </div>

            <div className="page_body">
                {loading ? (
                    <div className="empty_state"><span className="loading"></span></div>
                ) : (
                    <div className="profile_layout">

                        {/* Hero */}
                        <div className="profile_hero">
                            <div className="avatar_wrap">
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="avatar" />
                                ) : (
                                    <div className="avatar_fallback"><UserRound size="34" /></div>
                                )}
                                <button className="edit_avatar" onClick={() => setEditing(true)} title="Edit profile">
                                    <Pencil size="13" />
                                </button>
                            </div>
                            <div className="hero_meta">
                                <h2>{user?.username}</h2>
                                <span className="hero_email"><Mail size="13" /> {user?.email}</span>
                                <div className="badge_row">
                                    <span className="badge">{user?.role}</span>
                                    <span className={`badge ${user?.accountStatus}`}>{user?.accountStatus}</span>
                                </div>
                            </div>
                        </div>

                        {/* Account info */}
                        <div className="profile_section">
                            <div className="section_title">Account Info</div>
                            <div className="info_grid">
                                <div className="info_card">
                                    <span className="info_label"><ShieldCheck size="12" /> Role</span>
                                    <span className="info_value">{user?.role}</span>
                                </div>
                                <div className="info_card">
                                    <span className="info_label"><MonitorSmartphone size="12" /> Status</span>
                                    <span className="info_value">{user?.accountStatus}</span>
                                </div>
                                <div className="info_card">
                                    <span className="info_label"><Blocks size="12" /> Linked Apps</span>
                                    <span className="info_value">{user?.linkedApps?.length ?? 0}</span>
                                </div>
                                <div className="info_card">
                                    <span className="info_label"><Globe size="12" /> Known IPs</span>
                                    <span className="info_value">{user?.ipAddresses?.length ?? 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* Edit form */}
                        <div className="profile_section">
                            <div className="section_title">Edit Profile</div>
                            {!editing ? (
                                <div className="edit_card">
                                    <button className="pill_button ghost" onClick={() => setEditing(true)}>
                                        <Pencil size="16" /> Edit username & avatar
                                    </button>
                                </div>
                            ) : (
                                <div className="edit_card">
                                    <form onSubmit={handleSave}>
                                        <div className="field_container">
                                            <label htmlFor="username">Username</label>
                                            <div className="input">
                                                <input
                                                    id="username"
                                                    type="text"
                                                    minLength={6}
                                                    maxLength={15}
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <span className="field_hint">6–15 characters, letters, numbers, and underscores only.</span>
                                        </div>
                                        <div className="field_container">
                                            <label htmlFor="avatar">Avatar URL</label>
                                            <div className="input">
                                                <input
                                                    id="avatar"
                                                    type="url"
                                                    placeholder="https://example.com/avatar.png"
                                                    value={avatar}
                                                    onChange={(e) => setAvatar(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ display: "flex",  alignItems: "center", gap: "10px", marginTop: "10px" }}>
                                            <button className="submit" type="submit" disabled={saving} style={{ flex: 1 }}>
                                                {saving ? <span className="loading"></span> : <>Save Changes <Save size="18" /></>}
                                            </button>
                                            <button className="pill_button ghost" type="button" onClick={cancelEdit} disabled={saving}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>

                        {/* Danger zone */}
                        <div className="profile_section">
                            <div className="section_title">Security</div>
                            <div className="danger_zone">
                                <div className="danger_text">
                                    <h4>Manage your devices</h4>
                                    <p>Review where you're signed in or revoke access from a device you don't recognize.</p>
                                </div>
                                <button className="pill_button" onClick={() => navigate("/devices")}>
                                    <MonitorSmartphone size="16" /> Devices
                                </button>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    )
}

export default Profile;
